<template>
  <mf-card-container v-model="searchFilter" title="Histórico" use-search placeholder="Buscar pelo consumidor">
    <template v-slot:filters>
      <v-tooltip :disabled="hasPiiAccess" max-width="250" top content-class="truncated-tooltip">
        <template v-slot:activator="{ on }">
          <span v-on="on">
            <export-cashback-consumption-dialog :filters="filtersVariable" :disabled="!hasPiiAccess" />
          </span>
        </template>
        <span>Você precisa ter a permissão de acesso a informações privilegiadas para acessar esse recurso.</span>
      </v-tooltip>
    </template>
    <paginated-data-table
      ref="table"
      :headers="headers"
      :search="searchFilter"
      :query="query"
      :custom-items-per-page="10"
      queryName="tabularConsumedCashback"
      queryMainPropertyName="data"
      :queryVariables="queryVariables"
      :graphql-uri="$microservicesUrls['cashback']"
      default-table-sort-by="DATA"
      :default-table-sort-desc="true"
      :skip="skipQuery"
      :loading="skipQuery"
      @update-options="updateVariables"
    >
      <template v-slot:item="{ item, index }">
        <tr>
          <td>
            <router-link v-if="hasPiiAccess" class="link" :to="{ path: `/clientes/detalhes-cliente/${item.customer.NUM_CPF_CNPJ}` }">
              {{ item.customer.NUM_CPF_CNPJ | cpfCnpj }}
            </router-link>
            <v-tooltip v-else max-width="250" top content-class="truncated-tooltip">
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  {{ item.customer.NUM_CPF_CNPJ }}
                </span>
              </template>
              <span>Você precisa ter acessos a dados sensíveis para poder ver essa informação.</span>
            </v-tooltip>
          </td>
          <td>
            <v-tooltip v-model="showTooltipStoreName[index]" max-width="250" top content-class="truncated-tooltip">
              <template v-slot:activator="{ on }">
                <span v-on="on">{{ item.store.NOM_NOME | truncate({ length: 150 }) | hifen }}</span>
              </template>
              <span>{{ item.store.NOM_NOME | hifen }}</span>
            </v-tooltip>
          </td>
          <td>{{ formatCurrency(item.cashback) | hifen }}</td>
          <td>{{ item.date || hifen }}</td>
        </tr>
      </template>
    </paginated-data-table>
  </mf-card-container>
</template>

<script>
import { mapState } from 'vuex'
import ExportCashbackConsumptionDialog from '@/modules/mobile/cashback/views/components/ExportCashbackConsumptionDialog.vue'
import { TABULAR_CONSUMED_CASHBACK } from '@/modules/mobile/cashback/graphql'
import { assertArray } from '@/config/helpers.js'

export default {
  name: 'ConsumedCashbackMetrics',
  components: {
    ExportCashbackConsumptionDialog,
    PaginatedDataTable: () => import('@/components/PaginatedDataTable.vue'),
  },
  data: () => ({
    searchFilter: '',
    newQueryVariables: {
      sort: [],
    },
    showTooltipStoreName: {},
    exportingCsv: false,
  }),
  computed: {
    ...mapState({
      stateFilters: (state) => state.filters,
    }),

    filtersVariable() {
      const { data, units } = this.stateFilters
      const [startDate, endDate] = assertArray(data)
      const filters = []

      if (startDate) filters.push({ type: 'DATE', value: startDate, predicate: 'GREATER_THAN_OR_EQUALS' })
      if (endDate) filters.push({ type: 'DATE', value: endDate, predicate: 'LESS_THAN_OR_EQUALS' })
      if (assertArray(units).length) filters.push({ type: 'STORE', value: units, predicate: 'IN' })

      return filters
    },

    queryVariables() {
      const variables = {
        filters: this.filtersVariable,
      }

      if (this.newQueryVariables.sort.length) {
        variables.sort = this.newQueryVariables.sort
      }

      return variables
    },

    headers() {
      return [
        {
          text: 'CPF',
          value: 'customer.NUM_CPF_CNPJ',
          width: '20%',
          sortable: true,
        },
        {
          text: 'Loja',
          value: 'store.NOM_NOME',
          width: '20%',
          sortable: true,
        },
        {
          text: 'Cashback consumido',
          value: 'cashback',
          width: '24%',
          sortable: true,
        },
        {
          text: 'Data do uso',
          value: 'DATA',
          width: '16%',
          sortable: true,
        },
      ]
    },

    query() {
      return TABULAR_CONSUMED_CASHBACK
    },

    hasPiiAccess() {
      return this.$hasPiiAccess()
    },

    skipQuery() {
      const { data, units } = this.stateFilters
      const [startDate, endDate] = assertArray(data)
      return !units?.length || !startDate || !endDate
    },
  },
  methods: {
    formatCurrency(currency) {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(currency)
    },

    updateVariables(optionsVariables) {
      const { sortBy } = optionsVariables

      if (sortBy.length) {
        const labelsSortBy = {
          'customer.NUM_CPF_CNPJ': 'CPF',
          'store.NOM_NOME': 'STORE',
          cashback: 'TOTAL_CASHBACK',
          date: 'DATA',
        }

        this.newQueryVariables.sort = sortBy.map((sortItem) => labelsSortBy?.[sortItem]).filter((sortItem) => sortItem)
      } else {
        this.newQueryVariables.sort = []
      }
    },

    async getDataToExport() {
      try {
        this.exportingCsv = true
        const { data } = await this.$apollo.query({
          query: TABULAR_CONSUMED_CASHBACK,
          variables: {
            page: 1,
            pageSize: 0,
            filters: this.filtersVariable,
            search: '',
            sortDirection: [],
            sort: [],
          },
          fetchPolicy: 'no-cache',
          context: {
            uri: this.$microservicesUrls['cashback'],
          },
        })
        const headers = ['CPF', 'Loja', 'Cashback consumido', 'Data do uso']

        const rows = data?.tabularConsumedCashback?.data.map((consumedCashback) => [
          consumedCashback.customer.NUM_CPF_CNPJ,
          consumedCashback.store.NOM_NOME,
          this.formatCurrency(consumedCashback.cashback, true)?.replaceAll(',', '.'),
          consumedCashback.date,
        ])

        return { headers, rows }
      } catch {
        this.$snackbar({ message: 'Falha na exportação de histórico' })
      } finally {
        this.exportingCsv = false
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.blue-action-text {
  color: $marca-0-l40;
  text-decoration: underline;
  font-size: 14px;
  font-weight: 400;
  font-family: 'Lato';
  text-align: left;
  &:hover {
    cursor: pointer;
  }
}
.report-icon {
  bottom: 7px;
}
</style>
